import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Printer, Award, Leaf } from "lucide-react";
import { api, apiError } from "../lib/api";

export default function Certificate() {
  const navigate = useNavigate();
  const [cert, setCert] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/course/certificate")
      .then(({ data }) => setCert(data))
      .catch((e) => setError(apiError(e)));
  }, []);

  if (error) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center">
        <p className="text-[#C47A53] font-medium">{error}</p>
        <button onClick={() => navigate("/dashboard")} className="mt-4 px-5 py-2.5 rounded-full bg-[#2C4A3E] text-[#FAF8F5]">Back to dashboard</button>
      </div>
    );
  }
  if (!cert) return <div className="max-w-3xl mx-auto px-4 py-16 text-[#5A6B61]">Preparing certificate…</div>;

  const date = new Date(cert.completion_date).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex items-center justify-between mb-6 no-print">
        <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-sm text-[#2C4A3E]" data-testid="cert-back">
          <ArrowLeft className="w-4 h-4" /> Dashboard
        </button>
        <button onClick={() => window.print()} data-testid="print-certificate-btn" className="px-6 py-3 rounded-full bg-[#2C4A3E] text-[#FAF8F5] font-semibold flex items-center gap-2 hover:bg-[#223B31]">
          <Printer className="w-4 h-4" /> Print / Save PDF
        </button>
      </div>

      <div className="certificate-print">
        <div className="relative max-w-4xl mx-auto bg-[#FAF7F0] border-[12px] border-double border-[#2C4A3E] rounded-xl shadow-2xl p-8 sm:p-14 text-center font-serif overflow-hidden">
          <div className="absolute top-4 left-4"><Leaf className="w-10 h-10 text-[#829B88]/40" /></div>
          <div className="absolute bottom-4 right-4"><Leaf className="w-10 h-10 text-[#829B88]/40 rotate-180" /></div>

          <div className="flex justify-center">
            <span className="w-16 h-16 rounded-full bg-[#2C4A3E] flex items-center justify-center">
              <Award className="w-8 h-8 text-[#C69255]" />
            </span>
          </div>
          <p className="mt-6 text-xs uppercase tracking-[0.3em] font-mono text-[#829B88]">Mystic Brew Academy</p>
          <h1 className="mt-3 text-3xl sm:text-5xl text-[#1A2621]">Certificate of Completion</h1>
          <p className="mt-6 text-[#5A6B61] italic text-lg">This certifies that</p>
          <p data-testid="certificate-name" className="mt-2 text-3xl sm:text-4xl font-semibold text-[#2C4A3E] border-b-2 border-[#C69255]/50 inline-block px-8 pb-2">
            {cert.student_name}
          </p>
          <p className="mt-6 text-[#334139] text-lg max-w-2xl mx-auto leading-relaxed">
            has successfully completed all ninety chapters and passed the final certification exam of the
          </p>
          <p className="mt-2 text-2xl sm:text-3xl text-[#1A2621]">{cert.course_title}</p>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-8 sm:gap-20">
            <div className="text-center">
              <p className="text-xl text-[#2C4A3E]">{date}</p>
              <div className="w-40 border-t border-[#2C4A3E]/40 mt-1" />
              <p className="text-xs font-mono uppercase tracking-widest text-[#829B88] mt-1">Date</p>
            </div>
            <div className="text-center">
              <p className="text-xl text-[#2C4A3E] italic">Mystic Brew Academy</p>
              <div className="w-40 border-t border-[#2C4A3E]/40 mt-1" />
              <p className="text-xs font-mono uppercase tracking-widest text-[#829B88] mt-1">Issued by</p>
            </div>
          </div>

          <p className="mt-8 text-xs font-mono text-[#829B88]">Certificate ID · {cert.certificate_id}{cert.final_exam_score != null ? ` · Final Score ${cert.final_exam_score}%` : ""}</p>
          <p className="mt-4 text-[10px] text-[#829B88] max-w-2xl mx-auto">
            This certificate recognizes the completion of an educational program. It is not a license to practice
            medicine and does not constitute medical accreditation. Government licensing or certification is not
            required to use this credential, as there are no federal or state licensing or certification boards
            for herbalists in the United States.
          </p>
        </div>
      </div>
    </div>
  );
}
